import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PubSub } from "aws-amplify";
import { useTranslation } from "react-i18next";
import { Accordion, Card } from "react-bootstrap";

import Icons from "../../utils/components/icons";

import DropDown from "../../utils/components/common/dropdown/dropdown";
import GuageChart from "../../utils/components/common/guageChart";
import DeviceDetail from "./DeviceDetail";
import CustomToggle from "./CustomToggle";
import { OrganisationContext, UserContext } from "../../layouts/MainLayout";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";

import { performModeChange } from "../../services/DeviceService";
import { backendConfig } from "../../config/BackendConfig";
import { USER_ROLES } from "../../utils/constants/userConstants";
import { getModeValue } from "../../utils/constants/deviceConstants";
import {
  beforeAfterDecimal,
  cartridgeColor,
  convertMilliseconds,
  formateWithToFixed,
  formatingTime,
  getDeviceLocation,
  modes,
  modeVal,
} from "../../utils/constants/serviceToolConstant";
import {
  cartridgeColorFinder,
  formatDate,
  modeChecker,
  round5ForCatridge,
  valueFinderByModes,
} from "../../utils/utils";

const DeviceInfo = ({ selectedDeviceDetails, setIsLoading }) => {
  const { featureEnabledStatus, loggedInUserRole } = useContext(UserContext);
  const { siteData } = useContext(OrganisationContext);
  const { t } = useTranslation("serviceTool");

  const [isDeviceModeErrorModal, setIsDeviceModeErrorModal] = useState(false);
  const [deviceData, setDeviceData] = useState();
  const [isDetail, setIsDetail] = useState(false);
  const [accordionStatus, setAccordionStatus] = useState({
    ClO2: false,
    Plant: false,
  });

  let changeModeLoadingTimeout = null;
  const changeModeSub = useRef(null);

  // Update device data state when device changes
  useEffect(() => {
    if (selectedDeviceDetails.length) {
      setDeviceData(selectedDeviceDetails[0]);
    }
  }, [selectedDeviceDetails]);

  // Mode list object
  const getModeList = () => {
    const modeList = [
      { label: "Standby", value: "STANDBY" },
      { label: "Sense Only", value: "SENSE_ONLY" },
      { label: "Operational", value: "OPERATIONAL" },
    ];
    if (featureEnabledStatus.researchMode) {
      modeList.push({ label: "Research", value: "RESEARCH" });
    }
    return modeList;
  };

  const offlineMode = [{ label: "Offline", value: "OFFLINE" }];

  // Item render
  const itemRender = useCallback((label, value) => {
    return (
      <div className="label-value-item">
        <h4 className="text-h3 mb-0 label-txt">
          {label} <span className="text-h3">:</span>
        </h4>
        <span className="text-h1 value-txt">{value}</span>
      </div>
    );
  }, []);

  // Accordion on off by clicking
  const accordionToggle = (key) => {
    setAccordionStatus({
      ...accordionStatus,
      [key]: !accordionStatus[key],
    });
  };

  // ClO2 Parameters tabel data
  const getTableData = useCallback(() => {
    if (!deviceData) return;
    let clorArr = ["sgp4x1", "sgp4x2", "sgp4x3", "sgp4x4"];

    let res = clorArr?.map((val, idx) => {
      let item = deviceData?.clo2Param?.[val];
      let isActiveClass = val === "sgp4x2" ? "active-row-item" : "";
      let isPlayIcon = val === "sgp4x2" ? <span>{Icons.play}</span> : "";
      return (
        <tr key={`1`} className={isActiveClass}>
          <td />
          <td>{isPlayIcon}</td>
          <td>
            <span className="heading-h4">{val}</span>
          </td>
          <td>
            <span className="heading-h4">{item?.clo2Calibration || "-"}</span>
          </td>
          <td>
            <span className="heading-h4">{item?.VoC || "-"}</span>
          </td>
          <td>
            <span className="heading-h4">{item?.NOx || "-"}</span>
          </td>
          <td>
            <span className="heading-h4">{item?.clo2 || "-"}</span>
          </td>
          <td>
            <span className="heading-h4">{item?.state || "-"}</span>
          </td>
          <td />
        </tr>
      );
    });

    return res;
  }, [deviceData]);

  // Change mode loading start
  const setChangeModeLoading = () => {
    clearTimeout(changeModeLoadingTimeout);
    setIsLoading(true);
    changeModeLoadingTimeout = setTimeout(cancelChangeModeLoading, 15000);
  };

  // Change mode loading stop
  const cancelChangeModeLoading = () => {
    setIsDeviceModeErrorModal(true);
    setIsLoading(false);
  };

  // Change device mode handel
  const changeDeviceMode = useCallback(
    async (mode, deviceDetails) => {
      setIsLoading(true);
      const queryParams = {
        siteId: siteData.id,
        deviceId: deviceDetails?.DeviceUUID,
        mode: modeVal[mode],
      };
      try {
        const response = await performModeChange(queryParams);
        if (response?.data?.statusCode === "SUCCESS") {
          setChangeModeLoading();
          const url = `chorus/${backendConfig.PUBSUB_ENV}/site/${siteData.id}/notification/events`;

          if (changeModeSub.current) {
            await changeModeSub.current.unsubscribe();
          }

          changeModeSub.current = PubSub.subscribe(url).subscribe({
            next: (data) => {
              clearTimeout(changeModeLoadingTimeout);
              let changedModeVal = data?.value?.mode;
              if (changedModeVal) {
                // deviceDetails.mode = getModeValue(modes[changedModeVal]);
                setDeviceData({
                  ...deviceData,
                  mode: getModeValue(modes[changedModeVal]),
                });
              } else {
                setIsDeviceModeErrorModal(true);
              }
              setIsLoading(false);
            },
            error: (error) => {
              setIsDeviceModeErrorModal(true);
              setIsLoading(false);
            },
            close: () => {},
          });
        } else {
          setIsDeviceModeErrorModal(true);
          setIsLoading(false);
        }
      } catch (e) {
        setIsLoading(false);
        setIsDeviceModeErrorModal(true);
      }
    },
    [siteData?.id]
  );

  // To Get formated location
  const getLocation = useMemo(() => {
    return getDeviceLocation(deviceData?.DeviceLocation);
  }, [deviceData?.DeviceLocation]);

  // Get before and after decimal value
  const beforeAfterValue = useCallback(
    (key) => {
      if (!deviceData) return;
      return beforeAfterDecimal(
        formateWithToFixed(deviceData[key]),
        deviceData?.mode
      );
    },
    [deviceData]
  );

  const getModeTime = useMemo(() => {
    const timeInMs = deviceData?.timeInModeMs || 0;
    const time = convertMilliseconds(timeInMs);
    return time;
  }, [deviceData?.timeInModeMs]);
  // Example usage
  formatingTime(getModeTime?.hours);
  formatingTime(getModeTime?.minutes);
  formatingTime(getModeTime?.seconds);
  console.log("okasde", getModeTime);

  //Need to change with dynamic value key for now is dummy
  let NaClremaining = 10;
  return (
    <div className="device-info-section">
      <div className="device-detail-parameters-seciton">
        {/* Device Details Sections start */}
        <div className="device-detail">
          <div>
            <div className="heading-section">
              <span className="heading-h2">{t("deviceDetail")}</span>
              {!isDetail && (
                <span
                  className="info-icon"
                  onClick={() => {
                    setIsDetail(true);
                  }}
                >
                  {Icons.deviceInfo}
                </span>
              )}
            </div>
            <div className="chorus-device-img">
              <img
                id="Chorus_Transparent_Device"
                src={require("../../assets/images/masked-chrous-device-img.png")}
                className="img-fluid device-image-info"
                alt="Chorus_Transparent_Device"
              />
            </div>
            <div className="device-text mt-2">
              <span className="text-h3 mb-3 d-flex">
                {deviceData?.DeviceName || "-"}{" "}
                <span className="vertical-line" />{" "}
                {deviceData?.ModelNumber || "-"}
              </span>
              <span className="text-h3 mb-3">{getLocation || "-"}</span>
              <span className="text-h2 mb-3 cu-ft">
                {Icons.measuringTape} 50 cu ft
              </span>
            </div>
          </div>
          {/* Device Details section start */}
          {isDetail && (
            <div className="device-detail-section">
              <DeviceDetail
                closeDetail={() => {
                  setIsDetail(false);
                }}
                deviceData={deviceData}
                location={getLocation}
              />
            </div>
          )}
          {/* Device Details section end */}
        </div>
        {/* Device Details Sections end */}

        {/* Device Parameters and graph sections start */}
        <div className="device-parameters">
          <div className="heading-section">
            <div>
              <span className="heading-h2">{t("deviceParameters")}</span>
              <span className="text-h2 last-update">
                Last Updated{" "}
                {formatDate(
                  deviceData?.timeStamp
                    ? new Date(deviceData?.timeStamp)
                    : new Date(),
                  false,
                  true
                )}
              </span>
            </div>
            <div className="mode-drop-section">
              <span className="text-h3">
                {t("modeTime")}{" "}
                <span className="text-h1 mode-time">
                  {getModeTime?.hours
                    ? `${formatingTime(getModeTime?.hours)}:`
                    : ""}
                  {formatingTime(getModeTime?.minutes)}:
                  {formatingTime(getModeTime?.seconds)}
                </span>
              </span>
              <DropDown
                options={
                  deviceData?.mode === "OFFLINE" ||
                  deviceData?.mode === undefined
                    ? offlineMode
                    : getModeList()
                }
                value={deviceData?.mode ?? "OFFLINE"}
                hideSelectedOption
                wrapperClass={
                  deviceData?.mode === "OFFLINE" ||
                  deviceData?.mode === undefined
                    ? "greyLight"
                    : "secondary"
                }
                identifier={deviceData}
                autoid={`changemode`}
                onSelection={changeDeviceMode}
                disabled={loggedInUserRole === USER_ROLES.viewOnlyUserCorp}
              />
            </div>
          </div>
          <div className="card graph-and-value-section">
            <div className="cardBody">
              <div className="graph">
                <GuageChart
                  id={`Device_GuageChart`}
                  targetValue={Math.round(deviceData?.target || "")}
                  additionalPercentage={30}
                  maxAdditionalPercentage={50}
                  value={
                    modeChecker(deviceData?.mode)
                      ? 0
                      : Math.round(deviceData?.clo2Value)
                  }
                  size="4.0x"
                  showIndicator
                  grayTheme
                  semiCircle={true}
                  mode={deviceData?.mode}
                />
                <div className="graphReading graph-text-reading">
                  <span className="text-h2">
                    {Icons.ppbIcon} {t("PPB")}
                  </span>
                  <span className="text-8">
                    {beforeAfterValue("clo2Value")?.before}
                    <span className="text-h2">
                      {beforeAfterValue("clo2Value")?.after}
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div className="value-right-side">
              <div className="item-card-section">
                <div className="item-card">
                  <div className="icon-div">
                    {Icons.barometerIcon}
                    <span className="text-8">
                      {beforeAfterValue("temperature")?.before}
                      <span className="text-h2 decimal-text">
                        {beforeAfterValue("temperature")?.after}
                        <span className="text-h2">&deg;c</span>
                      </span>
                    </span>
                  </div>
                  <span className="text-h2 txt">{t("temperature")}</span>
                </div>
                <div className="item-card">
                  <div className="icon-div">
                    {Icons.dropPercentIcon}
                    <span className="text-8">
                      {beforeAfterValue("humidity")?.before}
                      <span className="text-h2 decimal-text">
                        {beforeAfterValue("humidity")?.after}%
                      </span>
                    </span>
                  </div>
                  <span className="text-h2 txt">{t("humidity")}</span>
                </div>
                <div className="item-card">
                  <div className="icon-div">
                    {Icons.pressure}
                    <span className="text-8">
                      {beforeAfterValue("barometricPressure")?.before}
                      <span className="text-h2 decimal-text">
                        {beforeAfterValue("barometricPressure")?.after}mBar
                      </span>
                    </span>
                  </div>
                  <span className="text-h2 txt">{t("pressure")}</span>
                </div>
              </div>
              {/* cartridege graph section start */}
              <div className="cartridege-section">
                {Icons.testTubeIcon}
                <div
                  className={`cartridege-graph ${
                    deviceData?.cartridgeLevel < 20 ? "cartridege-less-20" : ""
                  }`}
                  title={`NaCl remaining  - ${NaClremaining} μl`}
                >
                  <span
                    className={`heading-h3 txt-div rangeLabel ${cartridgeColorFinder(
                      deviceData?.cartridgeLevel,
                      deviceData?.mode,
                      "deviceView"
                    )}`}
                  >
                    {valueFinderByModes(
                      deviceData?.mode,
                      "cartridge",
                      deviceData?.cartridgeLevel
                    )}
                  </span>

                  {+deviceData?.cartridgeLevel > 0 && (
                    <div
                      className="inner-div"
                      style={{
                        background: cartridgeColor(
                          round5ForCatridge(deviceData?.cartridgeLevel),
                          deviceData?.mode
                        ),
                        width: `${deviceData?.cartridgeLevel}%`,
                      }}
                    />
                  )}
                </div>
              </div>
              {/* cartridege graph section end */}
            </div>
          </div>
        </div>
        {/* Device Parameters and graph sections end */}
      </div>

      {/* Accordion for ClO2 Parameters and Plant Parameters start*/}
      <div className="accordion-section">
        <Accordion alwaysOpen={true}>
          <Card className={accordionStatus.ClO2 ? "accordion-opened" : ""}>
            <Card.Header>
              <CustomToggle
                eventKey="ClO2"
                onClick={() => accordionToggle("ClO2")}
                isOpen={accordionStatus.ClO2}
              >
                <span className="heading-h3" id="ClO2Head">
                  ClO2 Parameters
                </span>
              </CustomToggle>
            </Card.Header>
            <Accordion.Collapse eventKey="ClO2">
              <div className="ClO2-para-section">
                <table className="table">
                  <thead>
                    <tr>
                      <th />
                      <th />
                      <th>
                        <span className="heading-h3">Sensors</span>
                      </th>
                      <th>
                        <span className="heading-h3">ClO2 calibration</span>
                      </th>
                      <th>
                        <span className="heading-h3">Raw VoC</span>
                      </th>
                      <th>
                        <span className="heading-h3">Raw NOx</span>
                      </th>
                      <th>
                        <span className="heading-h3">ClO2</span>
                      </th>
                      <th>
                        <span className="heading-h3">State</span>
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>{getTableData()}</tbody>
                </table>
              </div>
            </Accordion.Collapse>
          </Card>
          <Card className={accordionStatus.Plant ? "accordion-opened" : ""}>
            <Card.Header>
              <CustomToggle
                eventKey="Plant"
                onClick={() => accordionToggle("Plant")}
                isOpen={accordionStatus.Plant}
              >
                <span className="heading-h3" id="PlantHead">
                  Plant Parameters
                </span>
              </CustomToggle>
            </Card.Header>
            <Accordion.Collapse eventKey="Plant">
              <div className="body-section">
                {itemRender(
                  "Plant Shot sz (μl)",
                  formateWithToFixed(deviceData?.plantParam?.plantShot)
                )}
                {itemRender(
                  "Plant State / s",
                  deviceData?.plantParam?.plantState || "-"
                )}
              </div>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </div>
      {/* Accordion for ClO2 Parameters and Plant Parameters end*/}

      {/* State control seciton start*/}

      <div className="state-control-section">
        <div className="card-head">
          <span className="heading-h3" id="StateControlHead">
            State Control
          </span>
        </div>
        <div className="body-section">
          {itemRender("Main State", deviceData?.stateParam?.mainState || "-")}
          {itemRender(
            "Aux Sensors State",
            deviceData?.stateParam?.auxSensorState || "-"
          )}
          {itemRender(
            "LED Control",
            deviceData?.stateParam?.ledControlState || "-"
          )}
          {itemRender(
            "Generator State",
            deviceData?.stateParam?.generatorState || "-"
          )}
          {itemRender(
            "Gas Sensors State",
            deviceData?.stateParam?.gasSensorState || "-"
          )}
        </div>
      </div>
      {/* State control seciton end*/}

      <ModalComponent
        idName="device_view_modal"
        isOpen={isDeviceModeErrorModal}
        onCloseModal={() => setIsDeviceModeErrorModal(false)}
        descriptionText={t("change_mode_error")}
      />
    </div>
  );
};

export default DeviceInfo;
